import React from 'react';
import { Row } from 'react-table';

import { formatNumberCompact } from '@trqk/utils';

import { footerHeight, COLUMN_IDS } from '../constants';

interface TrqkGapChannelFooterProps {
    rows: Row<TrqkGapChannel>[];
}

/**
 * Returns the count of a column's values for a given row.
 */
type GetCount = (row: Row<TrqkGapChannel>) => number;

interface GetIncludedFooterInput {
    /**
     * {@link GetCount}
     */
    getCount: GetCount;
    /**
     * Optional class names to apply to the wrapper div
     */
    wrapperClassNames?: string;
    /**
     * @defaultValue {@link formatNumberCompact}
     * @param value Number to format
     * @returns a string to display in the footer
     */
    formatter?: (value: number) => string;
}

/**
 * Returns a footer component that sums the total of a column's values for all rows where the column
 * is selected to be included in the gap analysis.
 * @param getCount a {@link GetCount} function
 * @param wrapperClassNames optional class names to apply to the wrapper div
 */
export function getIncludedFooter({
    getCount,
    wrapperClassNames,
    formatter = formatNumberCompact,
}: GetIncludedFooterInput): React.FC<TrqkGapChannelFooterProps> {
    return function IncludedFooter({ rows }: TrqkGapChannelFooterProps) {
        const sum = rows.reduce(
            (acc: number, row: Row<TrqkGapChannel>): number => {
                const checked =
                    row?.values?.[COLUMN_IDS.INCLUDE_CHANNEL]?.checked ?? false;

                if (checked) {
                    const count = getCount(row);

                    return acc + count;
                }

                return acc;
            },
            0,
        );

        const finalFormatter = formatter ?? formatNumberCompact;

        return (
            <div
                className={wrapperClassNames ?? 'ml-1 flex items-center'}
                style={{ height: footerHeight }}
            >
                <p className="text-center">{finalFormatter(sum)}</p>
            </div>
        );
    };
}
